import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp } from "lucide-react";
import { useTranslation } from "react-i18next";

// True when the page area behind the element has a dark background.
const isDarkBehind = (el) => {
  const r = el.getBoundingClientRect();
  const hit = document.elementsFromPoint(r.left + r.width / 2, r.top + r.height / 2).find((n) => !el.contains(n));
  for (let n = hit; n && n !== document.documentElement; n = n.parentElement) {
    const m = getComputedStyle(n).backgroundColor.match(/[\d.]+/g);
    if (m && (m[3] === undefined || +m[3] > 0.5)) return (0.2126 * m[0] + 0.7152 * m[1] + 0.0722 * m[2]) / 255 < 0.5;
  }
  return false;
};

export const BackToTop = () => {
  const [show, setShow] = useState(false);
  const [onDark, setOnDark] = useState(false);
  const ref = useRef(null);
  const { i18n } = useTranslation();
  const label = { nl: "Terug naar boven", fr: "Retour en haut", de: "Nach oben" }[i18n.language?.slice(0, 2)] || "Back to top";

  useEffect(() => {
    let frame = 0;
    let last = 0;
    let trailing = 0;
    // Background check is costly, so run it at most every 150ms plus once when scrolling stops.
    const checkContrast = () => {
      last = performance.now();
      if (ref.current) setOnDark(isDarkBehind(ref.current));
    };
    const update = () => {
      frame = 0;
      setShow(window.scrollY > 700);
      clearTimeout(trailing);
      if (performance.now() - last > 150) checkContrast();
      trailing = setTimeout(checkContrast, 120);
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(trailing);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  useEffect(() => {
    if (show && ref.current) setOnDark(isDarkBehind(ref.current));
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.button
          type="button"
          ref={ref}
          data-testid="back-to-top-button"
          data-theme={onDark ? "light" : "dark"}
          aria-label={label}
          title={label}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          initial={{ opacity: 0, y: 16, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.9 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className={`group fixed bottom-5 right-5 md:bottom-8 md:right-8 z-50 grid place-items-center w-12 h-12 rounded-full overflow-hidden ring-1 transition-colors duration-300 ${onDark ? "bg-[#ffffff] text-[#18181b] ring-black/5" : "bg-[#18181b] text-[#ffffff] ring-white/10"}`}
        >
          <span className={`absolute inset-0 ${onDark ? "bg-[#e4e4e7]" : "bg-[#3f3f46]"} translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]`} />
          <ArrowUp strokeWidth={1.75} className="relative w-5 h-5 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:-translate-y-0.5" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};
